import { Col, Text } from 'components';
import {
  createSpamDomain,
  getSpamDomains,
  removeSpamDomain,
  theme,
  updateSpamDomain
} from 'lib';
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import DomainModal from './DomainModal';
import DomainsTable from './DomainsTable';
import Pagination from './Pagination';
import { AccessDenied, ErrorState } from './ui';

const DEFAULT_FILTERS = { active: 'all', search: '' };
const FILTER_KEYS = Object.keys(DEFAULT_FILTERS);
const DEFAULT_LIMIT = 50;
// Lives inside the "Spam" top-level tab, on its "domains" sub-tab.
const TAB = 'spam';
const SUB_TAB = 'domains';

const selectCls =
  'h-[34px] border border-[#D2D8DF] rounded-lg px-2.5 text-[13px] text-[#353E4A] bg-white focus:outline-none focus:ring-2 focus:ring-[#FA3F58]/20';
const inputCls =
  'h-[34px] border border-[#D2D8DF] rounded-lg px-3 text-[13px] text-[#353E4A] bg-white focus:outline-none focus:ring-2 focus:ring-[#FA3F58]/20';
const labelCls = 'text-[10px] font-semibold text-[#97A3B5] uppercase tracking-wide';

/** Spam-domain blocklist: any guarded post containing a link to one of these is blocked. */
const DomainsTab = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const applied = FILTER_KEYS.reduce((acc, key) => {
    acc[key] = searchParams.get(key) || DEFAULT_FILTERS[key];
    return acc;
  }, {});
  const page = Number(searchParams.get('page')) || 1;
  const limit = Number(searchParams.get('limit')) || DEFAULT_LIMIT;
  const appliedKey = searchParams.toString();

  const [searchInput, setSearchInput] = useState(applied.search);
  const searchTimer = useRef(null);

  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const reqIdRef = useRef(0);

  const fetchDomains = async (f, p, lim) => {
    const reqId = ++reqIdRef.current;
    setLoading(true);
    setError(null);
    const res = await getSpamDomains({ ...f, page: p, limit: lim });
    if (reqId !== reqIdRef.current) return;

    if (res?.status === 403) {
      setForbidden(true);
      setLoading(false);
      return;
    }
    if (res?.err || !res?.data) {
      setError(res?.err || 'Could not load spam domains.');
      setLoading(false);
      return;
    }
    setItems(res.data.items || []);
    setPagination(res.pagination || null);
    setLoading(false);
  };

  useEffect(() => {
    fetchDomains(applied, page, limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedKey]);

  useEffect(() => {
    setSearchInput(applied.search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applied.search]);

  useEffect(() => () => searchTimer.current && clearTimeout(searchTimer.current), []);

  const writeParams = (updates) => {
    const next = { tab: TAB, sub: SUB_TAB };
    searchParams.forEach((v, k) => {
      next[k] = v;
    });
    Object.entries(updates).forEach(([k, v]) => {
      if (v === undefined || v === null || v === '' || v === 'all') delete next[k];
      else next[k] = String(v);
    });
    setSearchParams(next, { replace: true });
  };

  const handleFilterChange = (key, value) => writeParams({ [key]: value, page: 1 });

  const handleSearchChange = (value) => {
    setSearchInput(value);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => writeParams({ search: value, page: 1 }), 400);
  };

  const handleClear = () => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    setSearchInput('');
    setSearchParams({ tab: TAB, sub: SUB_TAB });
  };

  const handleToggleActive = async (domain) => {
    if (busyId) return;
    setBusyId(domain._id);
    const res = await updateSpamDomain(domain._id, !domain.active);
    setBusyId(null);

    if (res?.err || !res?.data) {
      dispatch({ type: 'HANDLE_RES', payload: { err: res?.err || 'Update failed.' } });
      return;
    }
    setItems((prev) =>
      prev.map((d) =>
        d._id === domain._id ? { ...d, active: res.data.active, updatedAt: res.data.updatedAt } : d
      )
    );
    dispatch({
      type: 'HANDLE_RES',
      payload: { res: `Domain ${res.data.active ? 'activated' : 'deactivated'}.` }
    });
  };

  const handleDelete = async (domain) => {
    if (busyId) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete "${domain.domain}" from the blocklist?`)) return;
    setBusyId(domain._id);
    const res = await removeSpamDomain(domain._id);
    setBusyId(null);

    if (res?.err) {
      dispatch({ type: 'HANDLE_RES', payload: { err: res.err || 'Delete failed.' } });
      return;
    }
    setItems((prev) => prev.filter((d) => d._id !== domain._id));
    setPagination((prev) => (prev ? { ...prev, total: Math.max(0, (prev.total || 1) - 1) } : prev));
    dispatch({ type: 'HANDLE_RES', payload: { res: 'Domain deleted.' } });
  };

  const handleSubmitModal = async ({ domain }) => {
    if (submitting) return;
    setSubmitting(true);
    const res = await createSpamDomain(domain);
    setSubmitting(false);

    if (res?.err || !res?.data) {
      dispatch({ type: 'HANDLE_RES', payload: { err: res?.err || 'Could not add the domain.' } });
      return;
    }

    dispatch({ type: 'HANDLE_RES', payload: { res: res.msg || 'Domain added.' } });
    setModalOpen(false);
    fetchDomains(applied, page, limit);
  };

  const total = pagination?.total ?? 0;
  const totalPages = total > 0 ? Math.max(1, Math.ceil(total / limit)) : 1;

  if (forbidden) return <AccessDenied />;

  return (
    <Col noFlex wid="100%">
      <div className="bg-white border border-[#D2D8DF] rounded-xl p-4 mb-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex flex-col gap-1">
            <label className={labelCls}>Status</label>
            <select
              value={applied.active}
              onChange={(e) => handleFilterChange('active', e.target.value)}
              className={selectCls}
            >
              <option value="all">All</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className={labelCls}>Search</label>
            <div className="relative flex items-center">
              <input
                type="text"
                value={searchInput}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder="domain starts with…"
                className={`${inputCls} pr-7 w-[220px] font-mono`}
              />
              {searchInput && (
                <button
                  type="button"
                  onClick={() => handleSearchChange('')}
                  className="absolute right-2 text-[#97A3B5] hover:text-[#353E4A] transition-colors"
                  aria-label="Clear search"
                >
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3 ml-auto">
            <Text c1 col={theme.GREY_60} style={{ fontSize: '12px' }}>
              {total} domains
            </Text>
            <button
              type="button"
              onClick={handleClear}
              className="h-[34px] px-4 rounded-lg border border-[#D2D8DF] text-[13px] text-[#5F6F86] font-medium hover:bg-[#F0F2F4] transition-colors"
            >
              Clear filters
            </button>
            <button
              type="button"
              onClick={() => setModalOpen(true)}
              className="h-[34px] px-4 rounded-lg bg-[#FA3F58] text-[13px] text-white font-semibold hover:opacity-90 transition-opacity"
            >
              Add domain
            </button>
          </div>
        </div>
      </div>

      {error ? (
        <ErrorState message={error} onRetry={() => fetchDomains(applied, page, limit)} />
      ) : (
        <>
          <DomainsTable
            rows={items}
            loading={loading}
            busyId={busyId}
            onToggleActive={handleToggleActive}
            onDelete={handleDelete}
          />
          {!loading && total > 0 && (
            <Pagination
              page={page}
              totalPages={totalPages}
              pageSize={limit}
              total={total}
              pageSizeOptions={[25, 50, 100]}
              onPageSizeChange={(size) => writeParams({ limit: size, page: 1 })}
              onPrev={() => writeParams({ page: page - 1 })}
              onNext={() => writeParams({ page: page + 1 })}
              onGoToPage={(p) => writeParams({ page: p })}
            />
          )}
        </>
      )}

      <DomainModal
        visible={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmitModal}
        submitting={submitting}
      />
    </Col>
  );
};

export default DomainsTab;
